import {
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiInbox, FiPlusCircle, FiSearch } from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useListNeedsQuery, type NeedDto } from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { organizationHasSeveralDepartments } from "../../common/departments/departmentVisibility";
import { urls } from "../../urls";
import { NeedStatusTag } from "./NeedStatusTag";
import { useCreateNeed } from "./useCreateNeed";
import { NEED_STATUS_FILTER_OPTIONS, useNeedStatusLabel } from "./useNeedStatusLabel";
import { useNeedPriorityLabel } from "./useNeedPriorityLabel";

interface Props {
  title: string;
  description: string;
  mineOnly: boolean;
  /** Sync the status filter with the URL `?status=` param (used by sidebar links to Inbox / Approved). */
  urlStatusSync: boolean;
}

export const NeedsListView = ({ title, description, mineOnly, urlStatusSync }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { me, selectedOrg } = useLoggedInWithOrgContextState();
  const showsDepartment = organizationHasSeveralDepartments(selectedOrg);
  const { create: createNeed, isCreating } = useCreateNeed();
  const statusLabel = useNeedStatusLabel();
  const priorityLabel = useNeedPriorityLabel();

  const [localStatus, setLocalStatus] = useState<string>("");
  const status = urlStatusSync ? (searchParams.get("status") ?? "") : localStatus;
  const [search, setSearch] = useState("");

  const { data, isLoading, isError } = useListNeedsQuery(status ? { status } : {});

  const needs = useMemo(() => {
    let result = data?.needs ?? [];
    if (mineOnly) result = result.filter((n) => n.createdBy.id === me.id);
    const q = search.trim().toLowerCase();
    if (q) result = result.filter((n) => (n.title ?? "").toLowerCase().includes(q));
    return result;
  }, [data?.needs, mineOnly, me.id, search]);

  const setStatus = (next: string) => {
    if (urlStatusSync) {
      const params = new URLSearchParams(searchParams);
      if (next) params.set("status", next);
      else params.delete("status");
      setSearchParams(params);
    } else {
      setLocalStatus(next);
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="flex-start" mb="6" gap="4" wrap="wrap">
        <Box>
          <Heading as="h1" size="lg" color="smText" mb="2">
            {title}
          </Heading>
          <Text color="smMuted">{description}</Text>
        </Box>
        <Button colorScheme="teal" leftIcon={<Icon as={FiPlusCircle} />} onClick={createNeed} isLoading={isCreating}>
          {t("Report a need")}
        </Button>
      </Flex>

      <Flex gap="3" mb="4" align="center" wrap="wrap">
        <InputGroup maxW="320px">
          <InputLeftElement pointerEvents="none">
            <Icon as={FiSearch} color="smMuted" />
          </InputLeftElement>
          <Input
            placeholder={t("Search by title…") ?? undefined}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>
        <Select
          maxW="240px"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          placeholder={t("All statuses") ?? undefined}
        >
          {NEED_STATUS_FILTER_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {statusLabel(s)}
            </option>
          ))}
        </Select>
      </Flex>

      {isLoading ? (
        <Center py="20">
          <Spinner color="smPrimary" />
        </Center>
      ) : isError ? (
        <Center py="20">
          <Text color="smMuted">{t("Could not load needs.")}</Text>
        </Center>
      ) : needs.length === 0 ? (
        <Center
          py="16"
          px="6"
          bg="smBackground"
          rounded="xl"
          border="1px solid"
          borderColor="smBorder"
          flexDirection="column"
          gap="3"
          color="smMuted"
        >
          <Icon as={FiInbox} boxSize="28px" />
          <Text fontWeight="medium" color="smText">
            {mineOnly ? t("You haven't reported any needs yet") : t("No needs match these filters")}
          </Text>
          <Button colorScheme="teal" size="sm" leftIcon={<Icon as={FiPlusCircle} />} onClick={createNeed} isLoading={isCreating}>
            {t("Report a need")}
          </Button>
        </Center>
      ) : (
        <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" overflow="hidden">
          <TableContainer>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>{t("Title")}</Th>
                  <Th>{t("Status")}</Th>
                  {showsDepartment && <Th>{t("Department")}</Th>}
                  <Th isNumeric>{t("Estimated value")}</Th>
                  <Th>{t("Priority")}</Th>
                  <Th>{t("Reported by")}</Th>
                  <Th>{t("Created")}</Th>
                  <Th>{t("Desired delivery")}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {needs.map((n) => (
                  <Tr
                    key={n.id}
                    cursor="pointer"
                    _hover={{ bg: "smBackgroundSecondary" }}
                    onClick={() =>
                      navigate(n.canEdit ? urls.app.needs.edit.go(n.id) : urls.app.needs.view.go(n.id))
                    }
                  >
                    <Td fontWeight="medium" color="smText">
                      {n.title?.trim() || t("Untitled")}
                    </Td>
                    <Td>
                      <NeedStatusTag status={n.status} />
                    </Td>
                    {showsDepartment && <Td color="smMuted">{n.department?.name ?? "–"}</Td>}
                    <Td color="smMuted" isNumeric>
                      {formatValue(n.estimatedValue?.value)}
                    </Td>
                    <Td color="smMuted">{n.priority ? priorityLabel(n.priority) : "–"}</Td>
                    <Td color="smMuted">{personName(n.createdBy)}</Td>
                    <Td color="smMuted">{formatIsoDate(n.createdAt)}</Td>
                    <Td color="smMuted">{n.deliveryDate ? formatLocalDate(n.deliveryDate) : "–"}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

const nokFormatter = new Intl.NumberFormat("nb-NO");
const formatValue = (value: number | null | undefined): string =>
  value == null ? "–" : `${nokFormatter.format(value)} NOK`;

const formatIsoDate = (instant: string): string => {
  const d = new Date(instant);
  return Number.isNaN(d.getTime())
    ? instant
    : `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};
const formatLocalDate = (yyyyMmDd: string): string => {
  const parts = yyyyMmDd.split("-");
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : yyyyMmDd;
};
const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

const personName = (p: NeedDto["createdBy"]): string => {
  const name = [p.firstName, p.lastName].filter(Boolean).join(" ").trim();
  return name || "–";
};
